/**
 * Poids de la note d'importance (C22 SP0 « Le poids pèse »).
 *
 * La note d'importance (1 à 5) est posée sur chaque passage marqué d'un concept.
 * L'échelle est PERSONNELLE : l'outil ne connaît pas le sens des paliers
 * (docs/MISE_EN_RELATION_DES_IDEES_REFLEXION.md § 3.6). Trois règles en découlent :
 *
 *  1. le poids est MONOTONE — une note plus haute pèse plus ;
 *  2. il est SANS SEUIL — le plancher est strictement positif, aucun palier
 *     n'est ignoré ;
 *  3. une marque SANS NOTE vaut un 3 — elle n'est pas punie.
 *
 * Ce fichier vit dans src/utils/ et est COPIÉ octet pour octet dans
 * api/src/utils/ (test de dérive : api/src/utils/importanceWeight.drift.test.ts).
 * Modifier ici, puis recopier. Aucun import : la copie serveur doit rester autonome.
 */

/** La valeur d'une marque sans note, et le centre du barème. */
export const IMPORTANCE_NEUTRE = 3;

/** Poids des notes 1 à 5 : linéaire autour du neutre, plancher strictement positif. */
const POIDS = [0.7, 0.85, 1, 1.15, 1.3];

const estNoteValide = (rating: unknown): rating is number =>
  typeof rating === 'number' && Number.isInteger(rating) && rating >= 1 && rating <= 5;

/** La note lue par tous les calculs : 1 à 5, et 3 pour une note absente ou invalide. */
export function noteEffective(rating?: number | null): number {
  return estNoteValide(rating) ? rating : IMPORTANCE_NEUTRE;
}

/** Poids d'une note : 0,70 · 0,85 · 1,00 · 1,15 · 1,30. */
export function poidsImportance(rating?: number | null): number {
  return POIDS[noteEffective(rating) - 1];
}

/** Comparateur de tri : les notes les plus hautes d'abord, 0 à égalité de valeur effective. */
export function comparerParImportance(a?: number | null, b?: number | null): number {
  return noteEffective(b) - noteEffective(a);
}

/**
 * La note du meilleur passage. À valeur égale, une note saisie l'emporte sur
 * une marque sans note ; si le meilleur passage n'a pas de note, le résultat
 * est `undefined` — la règle n'écrit jamais un 3 que personne n'a posé.
 */
export function meilleureNote(ratings: ReadonlyArray<number | null | undefined>): number | undefined {
  let meilleure: number | undefined;
  let valeur = 0;
  for (const rating of ratings) {
    const effective = noteEffective(rating);
    const saisie = estNoteValide(rating);
    if (effective > valeur || (effective === valeur && meilleure === undefined && saisie)) {
      valeur = effective;
      meilleure = saisie ? rating : undefined;
    }
  }
  return meilleure;
}

/**
 * Rang médian d'une note dans la distribution d'une personne, dans [0, 1] :
 * (marques strictement en dessous + la moitié des marques au même palier) / total.
 * `histogramme[i]` compte les marques de note effective `i + 1`.
 *
 * NON BRANCHÉE en SP0 : un 5 chez A n'est pas un 5 chez B, et c'est par rang
 * que deux échelles se compareront à la confrontation. Distribution vide → 0,5.
 */
export function rangDansDistribution(rating: number | null | undefined, histogramme: ReadonlyArray<number>): number {
  const compte = (i: number): number => Math.max(0, histogramme[i] ?? 0);
  let total = 0;
  for (let i = 0; i < 5; i++) total += compte(i);
  if (total <= 0) return 0.5;
  const palier = noteEffective(rating);
  let dessous = 0;
  for (let i = 0; i < palier - 1; i++) dessous += compte(i);
  return (dessous + compte(palier - 1) / 2) / total;
}

/**
 * En dessous de ce nombre de notes posées, on ne situe rien : une proportion sur
 * trop peu de notes ne dirait rien de l'échelle de la personne.
 */
export const ECHELLE_MIN = 10;

/**
 * Part des notes d'importance d'une personne STRICTEMENT en dessous de celle-ci,
 * dans [0, 1] — ce que dit la phrase « au-dessus de 30 % de ce que Claire a noté »
 * du « Face à face » (C23 SP2). `histogramme[i]` compte les notes POSÉES de valeur
 * `i + 1` : une marque sans note n'y entre pas (elle ne dit rien de l'échelle).
 *
 * `undefined` — donc aucune phrase — pour une note qui n'a pas été posée (absente,
 * hors de 1 à 5) et pour une échelle de moins d'`ECHELLE_MIN` notes.
 */
export function partEnDessous(rating: number | null | undefined, histogramme: ReadonlyArray<number>): number | undefined {
  if (!estNoteValide(rating)) return undefined;
  const compte = (i: number): number => Math.max(0, histogramme[i] ?? 0);
  let total = 0;
  for (let i = 0; i < 5; i++) total += compte(i);
  if (total < ECHELLE_MIN) return undefined;
  let dessous = 0;
  for (let i = 0; i < rating - 1; i++) dessous += compte(i);
  return dessous / total;
}
